import type { DocumentView } from "@procuremate/shared";
import { Download, Ellipsis, Trash2 } from "lucide-react";
import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { DocumentStatusIcon } from "./attachments";
import { documentDownloadUrl, useRemoveDocument } from "./documents-api";

function size(bytes: number, language: string): string {
  const format = new Intl.NumberFormat(language, { maximumFractionDigits: 1 });
  return bytes >= 1024 * 1024
    ? `${format.format(bytes / 1024 / 1024)} MB`
    : `${format.format(Math.max(1, bytes / 1024))} KB`;
}

/**
 * The request's documents in the live panel (WP-2.2): status as it is read, what it is (kind,
 * supplier, summary), download, and removal by the requester (it clears what the document supplied).
 * Removal sits in a "More actions" menu and asks for confirmation (WP-2.8: it was too easy to hit).
 */
export function DocumentList({
  requestId,
  documents,
  canRemove,
  removeBlocked,
}: {
  requestId: string;
  documents: DocumentView[];
  canRemove: boolean;
  removeBlocked: boolean;
}) {
  const { t, i18n } = useTranslation();
  const menuId = useId();
  const remove = useRemoveDocument(requestId);
  const [confirming, setConfirming] = useState<string | null>(null);
  // The "More actions" menu opens in place under its document, never over the next one.
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const confirmRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (confirming) confirmRef.current?.focus();
  }, [confirming]);

  return (
    <ul className="flex flex-col divide-y divide-border">
      {documents.map((d) => (
        <li key={d.id} className="py-2.5">
          <div className="flex items-start justify-between gap-2">
            <p className="min-w-0 text-sm font-medium break-words text-fg">{d.fileName}</p>
            <div className="flex shrink-0 items-center">
              <a
                href={documentDownloadUrl(requestId, d.id)}
                className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-sm font-semibold text-link hover:bg-bg"
                aria-label={t("requests.documents.download", { name: d.fileName })}
              >
                <Download aria-hidden="true" className="h-3.5 w-3.5" />
                {t("requests.documents.downloadShort")}
              </a>
              {canRemove && confirming !== d.id ? (
                <button
                  type="button"
                  data-more-actions={d.id}
                  disabled={removeBlocked}
                  aria-haspopup="menu"
                  aria-expanded={menuFor === d.id}
                  aria-controls={menuFor === d.id ? `${menuId}-${d.id}` : undefined}
                  aria-label={t("requests.documents.more", { name: d.fileName })}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-sm text-fg-muted hover:bg-bg hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={() => setMenuFor((m) => (m === d.id ? null : d.id))}
                >
                  <Ellipsis aria-hidden="true" className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          </div>
          {canRemove && menuFor === d.id && confirming !== d.id ? (
            <DocumentMenu
              id={`${menuId}-${d.id}`}
              name={d.fileName}
              onClose={() => {
                setMenuFor(null);
                document.querySelector<HTMLButtonElement>(`[data-more-actions="${d.id}"]`)?.focus();
              }}
              onRemove={() => {
                setMenuFor(null);
                remove.reset();
                setConfirming(d.id);
              }}
            />
          ) : null}
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-fg-muted">
            <DocumentStatusIcon status={d.status} />
            {/* Shared wording with the tray ("name: reason"), so capitalised here. */}
            <span className="inline-block first-letter:uppercase">
              {d.status === "FAILED"
                ? t(`requests.documents.problems.${d.failureReason ?? "processing_failed"}`)
                : d.status === "READY"
                  ? [
                      d.kind ? t(`requests.documents.kinds.${d.kind}`) : null,
                      d.supplier,
                      d.method === "OCR" ? t("requests.documents.ocr") : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : t(`requests.documents.status.${d.status}`)}
            </span>
            <span aria-hidden="true">·</span>
            <span className="tabular">{size(d.sizeBytes, i18n.language)}</span>
          </p>
          {d.status === "READY" && d.summary ? (
            <p className="mt-1 text-sm whitespace-pre-wrap break-words text-fg">{d.summary}</p>
          ) : null}
          {canRemove && confirming === d.id ? (
            <div className="mt-2 rounded-md border border-border bg-bg p-3">
              <p ref={confirmRef} tabIndex={-1} className="text-sm text-fg outline-none">
                {t("requests.documents.confirmRemove", { name: d.fileName })}
              </p>
              {remove.isError ? (
                <p role="alert" className="mt-1 text-sm text-danger">
                  {t("requests.documents.removeFailed")}
                </p>
              ) : null}
              <div className="mt-2 flex gap-2">
                <Button
                  variant="danger"
                  className="h-9 px-3"
                  aria-disabled={remove.isPending || undefined}
                  onClick={() => {
                    if (!remove.isPending) remove.mutate(d.id, { onSuccess: () => setConfirming(null) });
                  }}
                >
                  {t("requests.documents.remove")}
                </Button>
                <Button variant="secondary" className="h-9 px-3" onClick={() => setConfirming(null)}>
                  {t("requests.panel.cancel")}
                </Button>
              </div>
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** The document's actions, in place under it; Remove opens the confirmation (WP-2.8). */
function DocumentMenu({
  id,
  name,
  onClose,
  onRemove,
}: {
  id: string;
  name: string;
  onClose: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const item = useRef<HTMLButtonElement>(null);
  useEffect(() => item.current?.focus(), []);

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    }
  }

  return (
    <div
      id={id}
      role="menu"
      aria-label={t("requests.documents.more", { name })}
      onKeyDown={onKeyDown}
      className="mt-1 flex justify-end"
    >
      <button
        ref={item}
        type="button"
        role="menuitem"
        className="inline-flex min-h-10 items-center gap-2 rounded-md border border-border px-3 text-sm font-semibold text-danger hover:bg-danger-bg"
        onClick={onRemove}
      >
        <Trash2 aria-hidden="true" className="h-4 w-4" />
        {t("requests.documents.removeNamed", { name })}
      </button>
    </div>
  );
}
